import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SearchInput } from "@/components/ui/search-input";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import {
  ShortcutHandler,
  useShortcuts,
} from "@/components/providers/ShortcutProvider";
import {
  CommandPaletteMode,
  CommandPaletteOption,
  CommandPaletteSubMenuItem,
  SubMenuApplicationStatus,
  SubMenuConfig,
} from "@/store/zustand/commandPaletteStore";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckIcon,
  MinusIcon,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Issue } from "@/types/issues";
import { getAppliedStatusIconForIssues } from "@/utils/utils";

export const CommandPalette: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasKeyboardInput, setHasKeyboardInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const { registerShortcut } = useShortcuts();

  const selectedIssueIds = useIssueSelectionStore(
    (state) => state.selectedIssueIds,
  );

  const {
    close,
    getVisibleOptions,
    isOpen,
    mode,
    subMenuConfig,
    setSubMenuConfig,
    setMode,
  } = useCommandPalette();

  const filteredOptions = useMemo(() => {
    const optionsToShow = getVisibleOptions();
    return optionsToShow.filter((option) =>
      option?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, getVisibleOptions]);

  const filteredSubMenuOptions = useMemo(() => {
    return subMenuConfig?.items?.filter((item) =>
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, subMenuConfig]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
      setHasKeyboardInput(false);
    }
  }, [isOpen]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  // Add effect to handle auto-scrolling
  useEffect(() => {
    if (hasKeyboardInput && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, hasKeyboardInput]);

  const executeSubMenuAction = useCallback(
    (
      submenuConfig: SubMenuConfig | null,
      option: CommandPaletteSubMenuItem,
    ) => {
      if (!submenuConfig) return;

      submenuConfig.action(option.value);
      if (submenuConfig.autoClose ?? true) {
        close();
      }
    },
    [close],
  );

  const executeWithDialogCleanup = useCallback(
    (option: CommandPaletteOption | undefined, value?: string) => {
      if (!option) return;

      if (option.subMenuConfig) {
        setMode(option.subMenuConfig.mode);
        setSubMenuConfig(option.subMenuConfig);
        setSelectedIndex(0);
        return;
      } else {
        option.action?.(value);
        close();
      }
    },
    [close],
  );

  // on mouse move turn off keyboard input
  const handleMouseMove = useCallback(() => {
    setHasKeyboardInput(false);
    setSelectedIndex(0);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!hasKeyboardInput) {
            setHasKeyboardInput(true);
            setSelectedIndex(0);
          } else {
            setSelectedIndex((prev) =>
              // prioritize submenu first
              filteredSubMenuOptions?.[prev]
                ? prev < filteredSubMenuOptions.length - 1
                  ? prev + 1
                  : 0
                : prev < filteredOptions.length - 1
                  ? prev + 1
                  : 0,
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!hasKeyboardInput) {
            setHasKeyboardInput(true);
            setSelectedIndex(0);
          } else {
            setSelectedIndex((prev) =>
              filteredSubMenuOptions?.[prev]
                ? prev > 0
                  ? prev - 1
                  : filteredSubMenuOptions.length - 1
                : prev > 0
                  ? prev - 1
                  : filteredOptions.length - 1,
            );
          }
          break;
        case "Enter":
          e.preventDefault();
          // prioritize submenu first
          if (filteredSubMenuOptions?.[selectedIndex]) {
            executeSubMenuAction(
              subMenuConfig,
              filteredSubMenuOptions[selectedIndex],
            );
          } else if (filteredOptions[selectedIndex]) {
            executeWithDialogCleanup(filteredOptions[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;
      }
    },
    [
      filteredOptions,
      selectedIndex,
      hasKeyboardInput,
      executeWithDialogCleanup,
      close,
      subMenuConfig,
      filteredSubMenuOptions,
      executeSubMenuAction,
    ],
  );

  useEffect(() => {
    registerShortcut(
      "*",
      (e: KeyboardEvent) => {
        e.preventDefault();
      },
      { preventDefault: true },
    );
  }, [registerShortcut]);

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} modal={true}>
      <ShortcutHandler enabled={isOpen}>
        <DialogContent
          ref={dialogRef}
          className="sm:max-w-[640px] w-full p-0 gap-0 overflow-hidden bg-background border border-secondary-n-900 shadow-lg fixed! top-[10vh]! translate-y-0!"
          onKeyDown={handleKeyDown}
          onMouseMove={handleMouseMove}
          onClick={(e) => e.stopPropagation()}
          hideClose
          onEscapeKeyDown={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-secondary-n-900">
            <SearchInput
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
                setHasKeyboardInput(false);
              }}
              placeholder={
                mode !== CommandPaletteMode.DEFAULT && subMenuConfig?.searchText
                  ? subMenuConfig.searchText
                  : "Search commands..."
              }
              className="bg-secondary-n-950 placeholder:text-secondary-n-700"
            />
          </div>

          {mode === CommandPaletteMode.ITEM_SELECTION && subMenuConfig && (
            <>
              <div
                className="max-h-[320px] overflow-y-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                }}
              >
                {filteredSubMenuOptions?.map((item, index) => (
                  <div
                    key={item.value}
                    ref={
                      hasKeyboardInput && selectedIndex === index
                        ? selectedItemRef
                        : null
                    }
                    className={`flex items-center justify-between px-3 py-2 transition-colors ${
                      hasKeyboardInput
                        ? selectedIndex === index
                          ? "bg-secondary-n-800"
                          : ""
                        : "cursor-pointer hover:bg-secondary-n-800"
                    }`}
                    onClick={() => {
                      executeSubMenuAction(subMenuConfig, item);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-popover-foreground">
                        {item.icon}
                      </span>
                      <span className="text-sm text-popover-foreground">
                        {item.name}
                      </span>
                    </div>
                    <div className="text-xs text-secondary-n-400 ml-auto">
                      {getAppliedStatusIconForIssues(
                        Array.from(selectedIssueIds),
                        item.value,
                        subMenuConfig?.selectionStatusFilter ?? (() => false),
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary-n-800 transition-colors border-t border-secondary-n-900"
                onClick={() => {
                  setSubMenuConfig(null);
                  setSearchQuery("");
                  setSelectedIndex(0);
                  setHasKeyboardInput(false);
                  setMode(CommandPaletteMode.DEFAULT);
                }}
              >
                <ArrowLeftIcon className="size-4 text-secondary-n-400" />
                <div className="text-sm text-secondary-n-400">Back</div>
              </div>
            </>
          )}
          {mode === CommandPaletteMode.DATE_SELECTION && subMenuConfig && (
            <>
              <div className="max-h-[320px] overflow-y-auto">
                {filteredSubMenuOptions?.map((item, index) => (
                  <div
                    key={item.value}
                    ref={
                      hasKeyboardInput && selectedIndex === index
                        ? selectedItemRef
                        : null
                    }
                    className={`flex items-center justify-between px-3 py-2 transition-colors ${
                      hasKeyboardInput
                        ? selectedIndex === index
                          ? "bg-secondary-n-800"
                          : ""
                        : "cursor-pointer hover:bg-secondary-n-800"
                    }`}
                    onClick={() => {
                      executeSubMenuAction(subMenuConfig, item);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="flex items-center gap-2">
                        <span className="text-popover-foreground">
                          {item.icon}
                        </span>
                        <span className="text-sm text-popover-foreground">
                          {item.name}
                        </span>
                      </div>
                      <div className="text-xs text-secondary-n-400 ml-auto">
                        {item.value
                          ? new Date(item.value).toLocaleDateString()
                          : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-0">
                <div className="flex items-center justify-center m-0 pb-4">
                  <Calendar
                    mode="single"
                    onSelect={(date) => {
                      executeSubMenuAction(subMenuConfig, {
                        value: date?.toISOString() || null,
                        name: "Select Date",
                        icon: <CalendarIcon className="size-4" />,
                      });
                    }}
                    numberOfMonths={2}
                    className="m-0! p-0! [&_.rdp-months]:flex! [&_.rdp-months]:flex-row! [&_.rdp-months]:gap-0! [&_.rdp-month]:m-0! [&_.rdp-month]:p-0! [&_.rdp]:p-0! [&_.rdp-caption]:p-0! [&_.rdp-nav]:m-0! [&_.rdp-cell]:p-0! [&_.rdp-table]:m-0! [&_.rdp-head_cell]:p-0!"
                  />
                </div>
              </div>
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary-n-800 transition-colors border-t border-secondary-n-900"
                onClick={() => {
                  setSubMenuConfig(null);
                  setSearchQuery("");
                  setSelectedIndex(0);
                  setHasKeyboardInput(false);
                  setMode(CommandPaletteMode.DEFAULT);
                }}
              >
                <ArrowLeftIcon className="size-4 text-secondary-n-400" />
                <div className="text-sm text-secondary-n-400">Back</div>
              </div>
            </>
          )}
          {mode === CommandPaletteMode.DEFAULT && (
            <div
              className="max-h-[320px] overflow-y-auto"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
              }}
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={option?.name}
                  ref={
                    hasKeyboardInput && selectedIndex === index
                      ? selectedItemRef
                      : null
                  }
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-secondary-n-800 transition-colors ${
                    hasKeyboardInput && index === selectedIndex
                      ? "bg-secondary-n-800"
                      : ""
                  }`}
                  onClick={() => executeWithDialogCleanup(option)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-popover-foreground">
                      {option?.icon}
                    </span>
                    <span className="text-sm text-popover-foreground">
                      {option?.name + (option?.hasFollowUp ? " ..." : "")}
                    </span>
                  </div>
                  {option?.shortcut && (
                    <div className="flex items-center gap-1">
                      {option?.shortcut.map((key, i) => (
                        <span
                          key={i}
                          className="text-secondary-n-400 text-xs border border-secondary-n-700 px-1.5 py-0.5 rounded min-w-[20px] text-center uppercase"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </ShortcutHandler>
    </Dialog>
  );
};
